/**
 * Database Module
 * Uses Supabase for persistence
 */
import { supabase } from "./api";

export type Order = {
  id: string;
  userId: string;
  parcelId: string;
  productType: string;
  variantId: number;
  color?: string;
  size?: string;
  imageUrl: string;
  stripePaymentId: string;
  printfulOrderId?: number;
  status: string;
  errorMessage?: string;
  createdAt: string;
};

type OrderRow = {
  id: string;
  user_id: string;
  parcel_id: string;
  product_type: string;
  variant_id: number;
  color: string | null;
  size: string | null;
  image_url: string;
  stripe_payment_id: string;
  printful_order_id: number | null;
  status: string;
  error_message: string | null;
  created_at: string;
};

function rowToOrder(row: OrderRow): Order {
  return {
    id: row.id,
    userId: row.user_id,
    parcelId: row.parcel_id,
    productType: row.product_type,
    variantId: row.variant_id,
    color: row.color || undefined,
    size: row.size || undefined,
    imageUrl: row.image_url,
    stripePaymentId: row.stripe_payment_id,
    printfulOrderId: row.printful_order_id || undefined,
    status: row.status,
    errorMessage: row.error_message || undefined,
    createdAt: row.created_at,
  };
}

export type Parcel = {
  id: string;
  userId: string;
  createdAt: string;
  name?: string;
  thumbnail?: string;

  // Position
  re: number;
  im: number;
  zoom: number;

  // Rendering config (for exact visual replication)
  iterations: number;
  colorScheme: string;
  reverseColors: boolean;
  smoothColoring: boolean;
  paletteMinIter: number;
  paletteMaxIter: number;
  scaleWithIterations: boolean;
  shiftHueAmount: number;
  saturateAmount: number;
  lightenAmount: number;
  colorSpace: number;

  // Claim fields
  claimed: boolean;
  claimedAt?: string;
  displayName?: string;
  stripePaymentId?: string;
  thumbnailUrl?: string;
};

// Database row type (snake_case)
type ParcelRow = {
  id: string;
  user_id: string;
  created_at: string;
  name: string | null;
  thumbnail: string | null;
  re: number;
  im: number;
  zoom: number;
  iterations: number;
  color_scheme: string;
  reverse_colors: boolean;
  smooth_coloring: boolean;
  palette_min_iter: number;
  palette_max_iter: number;
  scale_with_iterations: boolean;
  shift_hue_amount: number;
  saturate_amount: number;
  lighten_amount: number;
  color_space: number;
  // Claim fields
  claimed: boolean;
  claimed_at: string | null;
  display_name: string | null;
  stripe_payment_id: string | null;
  thumbnail_url: string | null;
};

/**
 * Convert database row (snake_case) to Parcel (camelCase)
 */
function rowToParcel(row: ParcelRow): Parcel {
  return {
    id: row.id,
    userId: row.user_id,
    createdAt: row.created_at,
    name: row.name || undefined,
    thumbnail: row.thumbnail || undefined,
    re: row.re,
    im: row.im,
    zoom: row.zoom,
    iterations: row.iterations,
    colorScheme: row.color_scheme,
    reverseColors: row.reverse_colors,
    smoothColoring: row.smooth_coloring,
    paletteMinIter: row.palette_min_iter,
    paletteMaxIter: row.palette_max_iter,
    scaleWithIterations: row.scale_with_iterations,
    shiftHueAmount: row.shift_hue_amount,
    saturateAmount: row.saturate_amount,
    lightenAmount: row.lighten_amount,
    colorSpace: row.color_space,
    claimed: row.claimed,
    claimedAt: row.claimed_at || undefined,
    displayName: row.display_name || undefined,
    stripePaymentId: row.stripe_payment_id || undefined,
    thumbnailUrl: row.thumbnail_url || undefined,
  };
}

/**
 * Convert Parcel (camelCase) to database insert (snake_case)
 */
function parcelToRow(
  parcel: Omit<Parcel, "id" | "createdAt">
): Omit<ParcelRow, "id" | "created_at"> {
  return {
    user_id: parcel.userId,
    name: parcel.name || null,
    thumbnail: parcel.thumbnail || null,
    re: parcel.re,
    im: parcel.im,
    zoom: parcel.zoom,
    iterations: parcel.iterations,
    color_scheme: parcel.colorScheme,
    reverse_colors: parcel.reverseColors,
    smooth_coloring: parcel.smoothColoring,
    palette_min_iter: parcel.paletteMinIter,
    palette_max_iter: parcel.paletteMaxIter,
    scale_with_iterations: parcel.scaleWithIterations,
    shift_hue_amount: parcel.shiftHueAmount,
    saturate_amount: parcel.saturateAmount,
    lighten_amount: parcel.lightenAmount,
    color_space: parcel.colorSpace,
    claimed: parcel.claimed,
    claimed_at: parcel.claimedAt || null,
    display_name: parcel.displayName || null,
    stripe_payment_id: parcel.stripePaymentId || null,
    thumbnail_url: parcel.thumbnailUrl || null,
  };
}

/**
 * Get parcels for a specific user
 */
export async function getParcels(userId: string): Promise<Parcel[]> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return [];
  }

  const { data, error } = await supabase
    .from("parcels")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[DB] Error fetching parcels:", error);
    return [];
  }

  return (data as ParcelRow[]).map(rowToParcel);
}

/**
 * Add a new parcel (save to collection)
 */
export async function addParcel(
  parcel: Omit<Parcel, "id" | "createdAt" | "claimed" | "claimedAt" | "displayName" | "stripePaymentId" | "thumbnailUrl">
): Promise<Parcel | null> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return null;
  }

  const row = parcelToRow({
    ...parcel,
    claimed: false,
  });

  const { data, error } = await supabase
    .from("parcels")
    .insert(row)
    .select()
    .single();

  if (error) {
    console.error("[DB] Error adding parcel:", error);
    return null;
  }

  return rowToParcel(data as ParcelRow);
}

/**
 * Delete a parcel by ID
 */
export async function deleteParcel(id: string): Promise<boolean> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return false;
  }

  const { error } = await supabase.from("parcels").delete().eq("id", id);

  if (error) {
    console.error("[DB] Error deleting parcel:", error);
    return false;
  }

  return true;
}

/**
 * Update a parcel
 */
export async function updateParcel(
  id: string,
  updates: Partial<Pick<Parcel, "name" | "thumbnail" | "displayName" | "thumbnailUrl">>
): Promise<Parcel | null> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return null;
  }

  // Convert camelCase updates to snake_case
  const rowUpdates: Record<string, unknown> = {};
  if (updates.name !== undefined) rowUpdates.name = updates.name || null;
  if (updates.thumbnail !== undefined)
    rowUpdates.thumbnail = updates.thumbnail || null;
  if (updates.displayName !== undefined)
    rowUpdates.display_name = updates.displayName || null;
  if (updates.thumbnailUrl !== undefined)
    rowUpdates.thumbnail_url = updates.thumbnailUrl || null;

  const { data, error } = await supabase
    .from("parcels")
    .update(rowUpdates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("[DB] Error updating parcel:", error);
    return null;
  }

  return rowToParcel(data as ParcelRow);
}

/**
 * Claim a parcel (mark as publicly claimed)
 */
export async function claimParcel(
  id: string,
  displayName: string,
  stripePaymentId: string
): Promise<Parcel | null> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return null;
  }

  const { data, error } = await supabase
    .from("parcels")
    .update({
      claimed: true,
      claimed_at: new Date().toISOString(),
      display_name: displayName,
      stripe_payment_id: stripePaymentId,
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("[DB] Error claiming parcel:", error);
    return null;
  }

  return rowToParcel(data as ParcelRow);
}

/**
 * Get all claimed parcels (for public gallery)
 */
export async function getClaimedParcels(
  options: { offset?: number; limit?: number; orderBy?: string } = {}
): Promise<Parcel[]> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return [];
  }

  const { offset = 0, limit = 20, orderBy = "claimed_at" } = options;

  let query = supabase
    .from("parcels")
    .select("*")
    .eq("claimed", true)
    .order(orderBy, { ascending: false })
    .range(offset, offset + limit - 1);

  const { data, error } = await query;

  if (error) {
    console.error("[DB] Error fetching claimed parcels:", error);
    return [];
  }

  return (data as ParcelRow[]).map(rowToParcel);
}

/**
 * Check if a coordinate is already claimed (with precision tolerance)
 */
export async function isCoordinateClaimed(
  re: number,
  im: number,
  zoom: number
): Promise<boolean> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return false;
  }

  // Round to 12 decimal places to match the DB unique index
  const roundedRe = Number(re.toFixed(12));
  const roundedIm = Number(im.toFixed(12));

  const { data, error } = await supabase
    .from("parcels")
    .select("id")
    .eq("claimed", true)
    .eq("zoom", zoom)
    .gte("re", roundedRe - 1e-12)
    .lte("re", roundedRe + 1e-12)
    .gte("im", roundedIm - 1e-12)
    .lte("im", roundedIm + 1e-12)
    .limit(1);

  if (error) {
    console.error("[DB] Error checking coordinate claim:", error);
    return false;
  }

  return (data?.length ?? 0) > 0;
}

/**
 * Upload a high-res merch image to Supabase Storage.
 * Returns the public URL for Printful to fetch.
 */
export async function uploadMerchImage(
  parcelId: string,
  productType: string,
  blob: Blob
): Promise<string | null> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return null;
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    console.error("[DB] Cannot upload merch image: no auth session");
    return null;
  }

  const filePath = `merch/${parcelId}_${productType}_${Date.now()}.png`;

  const { error: uploadError } = await supabase.storage
    .from("parcel-thumbnails")
    .upload(filePath, blob, {
      contentType: "image/png",
      upsert: false,
    });

  if (uploadError) {
    console.error("[DB] Error uploading merch image:", uploadError);
    return null;
  }

  const { data: urlData } = supabase.storage
    .from("parcel-thumbnails")
    .getPublicUrl(filePath);

  return urlData.publicUrl;
}

/**
 * Get orders for the current user
 */
export async function getOrders(): Promise<Order[]> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return [];
  }

  const { data, error } = await supabase
    .from("orders")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[DB] Error fetching orders:", error);
    return [];
  }

  return (data as OrderRow[]).map(rowToOrder);
}

/**
 * Upload a thumbnail to Supabase Storage
 */
export async function uploadThumbnail(
  parcelId: string,
  imageBlob: Blob
): Promise<string | null> {
  if (!supabase) {
    console.error("[DB] Supabase client not configured");
    return null;
  }

  // Debug: check auth state before upload
  const { data: { session } } = await supabase.auth.getSession();
  console.log("[DB] Upload auth state:", session ? `authenticated as ${session.user.id}` : "NOT AUTHENTICATED");
  console.log("[DB] Upload blob:", { size: imageBlob.size, type: imageBlob.type });

  if (!session) {
    console.error("[DB] Cannot upload thumbnail: no auth session");
    return null;
  }

  const filePath = `${parcelId}.png`;

  const { data: uploadData, error: uploadError } = await supabase.storage
    .from("parcel-thumbnails")
    .upload(filePath, imageBlob, {
      contentType: "image/png",
      upsert: true,
    });

  if (uploadError) {
    console.error("[DB] Error uploading thumbnail:", uploadError.message, uploadError);
    console.error("[DB] Upload details:", { filePath, blobSize: imageBlob.size, bucket: "parcel-thumbnails" });
    return null;
  }

  console.log("[DB] Upload succeeded:", uploadData);

  const { data: urlData } = supabase.storage
    .from("parcel-thumbnails")
    .getPublicUrl(filePath);

  const publicUrl = urlData.publicUrl;

  // Update parcel with thumbnail URL
  await updateParcel(parcelId, { thumbnailUrl: publicUrl });

  return publicUrl;
}
